// ./src/hooks/usePlayLists.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  PlayList,
  PlayListService,
  PlayListFilter,
  PlayListInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_DROPDOWN_PAGE_SIZE } from "@/config";

export interface UsePlayListsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<PlayListFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UsePlayListsReturn {
  // Data state
  playList: PlayList | null;
  playLists: PlayList[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<PlayListFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingPlayListId: string | null;
  selectedPlayList: PlayList | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<PlayListFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<PlayList[]>;
  create: (params: PlayListInput) => Promise<PlayList | null>;
  update: (id: string, params: PlayListInput) => Promise<PlayList | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<PlayListFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedPlayList: (playList: PlayList | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  playListsByStatus: Record<string, PlayList[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const usePlayLists = (
  options: UsePlayListsOptions = {}
): UsePlayListsReturn => {
  const {
    pageSize = DEFAULT_DROPDOWN_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [playList, setPlayList] = useState<PlayList | null>(null);
  const [playLists, setPlayLists] = useState<PlayList[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<PlayListFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingPlayListId, setRemovingPlayListId] = useState<string | null>(
    null
  );
  const [selectedPlayList, setSelectedPlayList] = useState<PlayList | null>(
    null
  );

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => playLists.length === 0 && !loading,
    [playLists.length, loading]
  );

  const playListsByStatus = useMemo(() => {
    return playLists.reduce((acc, playList) => {
      const status = String(playList.status || "unknown");
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(playList);
      return acc;
    }, {} as Record<string, PlayList[]>);
  }, [playLists]);

  // Helper function to update playlist in list if it exists, or add it if not
  const updateOrAddPlayListInList = useCallback((updatedPlayList: PlayList) => {
    setPlayLists((prev) => {
      const existingIndex = prev.findIndex((p) => p.id === updatedPlayList.id);
      if (existingIndex >= 0) {
        const newPlayLists = [...prev];
        newPlayLists[existingIndex] = updatedPlayList;
        return newPlayLists;
      } else {
        return [updatedPlayList, ...prev];
      }
    });
  }, []);

  // Helper function to remove playlist from list if it exists
  const removePlayListFromList = useCallback((playListId: string) => {
    setPlayLists((prev) => prev.filter((p) => p.id !== playListId));
  }, []);

  // Helper function to remove multiple playlists from list
  const removePlayListsFromList = useCallback((playListIds: string[]) => {
    const idsSet = new Set(playListIds);
    setPlayLists((prev) => prev.filter((p) => !idsSet.has(p.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setPlayList(null);
    setPlayLists([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingPlayListId(null);
    setSelectedPlayList(null);
  }, []);

  // Get single playlist (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await PlayListService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const playListData = result.data[0] as PlayList;
          setPlayList(playListData);
          updateOrAddPlayListInList(playListData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load playlist";
          setLastError(errorMessage);
          setPlayList(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load playlist";
        setLastError(errorMessage);
        setPlayList(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddPlayListInList]
  );

  // List playlists (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<PlayListFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setPlayLists([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await PlayListService.list(
          filter as PlayListFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newPlayLists = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setPlayLists((prev) => {
              const existingIds = new Set(prev.map((p) => p.id));
              const uniqueNewPlayLists = newPlayLists.filter(
                (p) => !existingIds.has(p.id)
              );
              return [...prev, ...uniqueNewPlayLists];
            });
          } else {
            setPlayLists(newPlayLists);
            // Auto-select first playlist if none selected and we have data
            if (reset && newPlayLists.length > 0) {
              setSelectedPlayList((prev) => (prev ? prev : newPlayLists[0]));
            }
          }

          setHasMore(newPlayLists.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load playlists";
          setLastError(errorMessage);
          if (!isAppending) setPlayLists([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load playlists";
        setLastError(errorMessage);
        if (!isAppending) setPlayLists([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many playlists by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<PlayList[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await PlayListService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedPlayLists = Array.isArray(result.data) ? result.data : [];
          loadedPlayLists.forEach((playList) => {
            updateOrAddPlayListInList(playList);
          });
          setLastError(null);
          return loadedPlayLists;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load playlists";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load playlists";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddPlayListInList]
  );

  // Create playlist (matching API method name)
  const create = useCallback(
    async (params: PlayListInput): Promise<PlayList | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await PlayListService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newPlayList = result.data[0] as PlayList;
          setPlayList(newPlayList);
          setPlayLists((prev) => [newPlayList, ...prev]);
          setLastError(null);
          return newPlayList;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create playlist";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create playlist";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update playlist (matching API method name)
  const update = useCallback(
    async (id: string, params: PlayListInput): Promise<PlayList | null> => {
      if (!id) {
        setLastError("PlayList ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await PlayListService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedPlayList = result.data[0] as PlayList;
          setPlayList(updatedPlayList);
          updateOrAddPlayListInList(updatedPlayList);
          setLastError(null);
          return updatedPlayList;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update playlist";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update playlist";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddPlayListInList]
  );

  // Remove playlist (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("PlayList ID is required for removal");
        return false;
      }

      setRemovingPlayListId(id);
      setLastError(null);

      try {
        const result = await PlayListService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setPlayList(null);
          removePlayListFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove playlist";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove playlist";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingPlayListId(null);
      }
    },
    [removePlayListFromList]
  );

  // Remove many playlists (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one PlayList ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await PlayListService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setPlayList(null);
          removePlayListsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove playlists";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove playlists";
        setLastError(errorMessage);
        return false;
      }
    },
    [removePlayListsFromList]
  );

  // Load more playlists
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh playlists
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    playList,
    playLists,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingPlayListId,
    selectedPlayList,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedPlayList,
    reset,
    playListsByStatus,
    lastError,
    clearError,
  };
};
